/**
 * TaxRegimeSelector — orchestrator for the cold-start tax-planning tool.
 *
 * Two phases:
 *   1. Wizard      — multi-step config-driven form (DEFAULT_PROFILE seeded)
 *   2. Result      — verdict, what-if sliders, opportunities, breakdowns
 *
 * The cold-start tool from `finmin_tool1_enhanced.html` is brought into the
 * FinminOS design system: Forest/Amber/Cream palette, Cormorant Garamond
 * display font, rounded-2xl cards with subtle hover lift, animated entrances.
 *
 * The "review-and-confirm" mode from the prototype is intentionally omitted
 * — that requires a prior-year filing on file, which we'll add when there's
 * a real backend endpoint serving last year's profile.
 */
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Calendar, Sparkles } from 'lucide-react';
import { Wizard } from './components/wizard';
import { ResultPanel } from './components/result-panel';
import { WIZARD_CONFIG, DEFAULT_PROFILE } from './wizard-config';
import { RULES_AY_2026_27 } from './rules';
import {
  computeRegimeTax, computeOpportunities, computeAdvanceTax,
} from './engine';
import type { ProfileInput, ProfileNumericKey } from './types';
import { PALETTE_FOREST, PALETTE_AMBER, PALETTE_CREAM } from './tokens';

type Phase = 'wizard' | 'result';

export function TaxRegimeSelector() {
  const [phase, setPhase]     = React.useState<Phase>('wizard');
  const [profile, setProfile] = React.useState<ProfileInput>(DEFAULT_PROFILE);

  // ── Generic profile setter (used by every input + what-if slider) ────────
  const handleChange = React.useCallback(
    <K extends keyof ProfileInput>(key: K, value: ProfileInput[K]) => {
      setProfile((p) => ({ ...p, [key]: value }));
    },
    [],
  );

  // ── Submit-from-wizard: zero hraReceived/rentPaid if hasHra was disabled ─
  function handleWizardSubmit() {
    if (!profile.hasHra) {
      setProfile((p) => ({ ...p, hraReceived: 0, rentPaid: 0 }));
    }
    setPhase('result');
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  function handleRestart() {
    setPhase('wizard');
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  // ── Live recomputation — every profile change triggers a recompute. ─────
  // Cheap (pure math, no async), so memoising on profile is enough to keep
  // the what-if sliders feeling instant.
  const { oldResult, newResult, opportunities, advanceTax } = React.useMemo(() => {
    // Apply the same hasHra zeroing during the result phase too, so toggling
    // it off retroactively works.
    const live: ProfileInput = profile.hasHra
      ? profile
      : { ...profile, hraReceived: 0, rentPaid: 0 };

    const oldR = computeRegimeTax(live, 'old', RULES_AY_2026_27);
    const newR = computeRegimeTax(live, 'new', RULES_AY_2026_27);
    const winnerKey: 'old' | 'new' = oldR.total <= newR.total ? 'old' : 'new';
    const winningResult = winnerKey === 'old' ? oldR : newR;
    return {
      oldResult:     oldR,
      newResult:     newR,
      opportunities: computeOpportunities(live, winnerKey, winningResult, RULES_AY_2026_27),
      advanceTax:    computeAdvanceTax(winningResult.total),
    };
  }, [profile]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: PALETTE_CREAM }}>

      {/* ── Page sub-header (back + breadcrumb badge) ──────────────── */}
      <div
        className="sticky top-16 z-10 border-b border-border/60 backdrop-blur-sm"
        style={{ backgroundColor: `${PALETTE_CREAM}E6` }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-70 shrink-0"
            style={{ color: PALETTE_FOREST }}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Dashboard
          </Link>
          <span className="text-border select-none" aria-hidden>/</span>
          <span className="text-sm font-semibold text-foreground truncate">
            Plan next year's tax
          </span>
          <span
            className="ml-auto shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
            style={{ backgroundColor: `${PALETTE_AMBER}18`, color: PALETTE_AMBER }}
          >
            <Sparkles className="h-3 w-3" aria-hidden />
            FY 2026-27
          </span>
        </div>
      </div>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      {phase === 'wizard' && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 sm:pt-10 pb-3">
          <p
            className="text-[10px] font-bold uppercase tracking-widest mb-2 inline-flex items-center gap-1.5"
            style={{ color: PALETTE_AMBER }}
          >
            <Calendar className="h-3 w-3" aria-hidden />
            Tax Regime Selector
          </p>
          <h1
            className="font-display font-semibold text-3xl sm:text-4xl leading-tight tracking-tight"
            style={{ color: PALETTE_FOREST }}
          >
            Plan next year's tax{' '}
            <em className="font-display italic" style={{ color: PALETTE_AMBER }}>before</em>{' '}
            it's too late.
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed max-w-2xl">
            Five steps. We model both regimes — surcharge, capital gains, and 87A marginal relief
            included — then show what specific actions would reduce your tax.
          </p>
        </div>
      )}

      {/* ── Body ─────────────────────────────────────────────────── */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {phase === 'wizard' ? (
          <Wizard
            config={WIZARD_CONFIG}
            profile={profile}
            onChange={handleChange}
            onSubmit={handleWizardSubmit}
          />
        ) : (
          <ResultPanel
            profile={profile}
            oldResult={oldResult}
            newResult={newResult}
            opportunities={opportunities}
            advanceTax={advanceTax}
            onWhatIfChange={(key: ProfileNumericKey, value) => handleChange(key, value)}
            onRestart={handleRestart}
          />
        )}
      </div>
    </div>
  );
}
